import "./BlogPage.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import headerImg from "../images/MediaPage_header.png";

const API_URL = process.env.REACT_APP_API_URL;

function BlogPage() {
    const navigate = useNavigate();
    const [featuredBlog, setFeaturedBlog] = useState(null);
    const [blogs, setBlogs] = useState([]);
    const [selectedTag, setSelectedTag] = useState("ALL");
    const [blogsLoading, setBlogsLoading] = useState(true);
    const [blogsError, setBlogsError] = useState("");
    const [showAllBlogs, setShowAllBlogs] = useState(false);

    const categoryFilters = [
        "ALL",
        "ASIA",
        "AFRICA",
        "NORTH AMERICA",
        "SOUTH AMERICA",
        "EUROPE",
        "AUSTRALIA",
        "MISC.",
    ];


    useEffect(() => {

        fetch(`${API_URL}/api/blogs/featured`)
            .then((response) => response.json())
            .then((data) => {
                console.log("Featured blog:", data);
                setFeaturedBlog(data);
            })
            .catch((error) => {
                console.error("Error fetching featured blog:", error);
            });

    }, []);

    useEffect(() => {
        const controller = new AbortController();
        const endpoint = selectedTag === "ALL"
            ? `${API_URL}/api/blogs`
            : `${API_URL}/api/blogs?tag=${encodeURIComponent(selectedTag)}`;

        setBlogsLoading(true);
        setBlogsError("");
        setShowAllBlogs(false);

        fetch(endpoint, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Unable to load blogs.");
                }
                return response.json();
            })
            .then((data) => setBlogs(Array.isArray(data) ? data : []))
            .catch((error) => {
                if (error.name !== "AbortError") {
                    console.error("Error fetching blogs:", error);
                    setBlogs([]);
                    setBlogsError("We couldn't load these stories. Please try again.");
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setBlogsLoading(false);
                }
            });

        return () => controller.abort();
    }, [selectedTag]);


    return (
        <div className="blog-page">
            <header className="blog-header">
                <img
                    src={headerImg}
                    alt="Blog header"
                    className="header-image-blog"
                />
                <div className="header-overlay" />
                <div className="header-text-blog">
                    <div className="intro-subtitle">MEDIA</div>
                    <h1 className="blog-title">Cultural Stories on Every Plate</h1>
                    <p className="blog-body">
                        Explore our blogs and podcasts that celebrate diverse cuisines and their tales to connect, learn, and experience different cultures, one delicious read or listen at a time.
                    </p>
                    <p className="blog-redirect"><a href="#blogs">Check out our latest blogs</a></p>
                </div>
            </header>

            <div className="blog-highlight">
                <h1>Highlights</h1>
                {/* The featured blog post will be dynamically loaded here */}
                <div className="featured-blog">
                    <div className="featured-blog-card">
                        {featuredBlog && (
                            <>
                                <img
                                    src={require(`../images/${featuredBlog.image}`)}
                                    alt={featuredBlog.title}
                                    className="featured-blog-image"
                                />

                                <div className="featured-blog-content">
                                    <div className="featured-blog-categories">
                                        {(featuredBlog.categories ?? []).map((category) => (
                                            <span key={category}>
                                                {category}
                                            </span>
                                        ))}
                                    </div>

                                    <h2>
                                        {featuredBlog.title}
                                    </h2>

                                    <p className="featured-blog-excerpt">
                                        {featuredBlog.excerpt}
                                    </p>

                                    <div className="featured-blog-footer">
                                        <div className="featured-blog-meta">
                                            <p className="featured-blog-author">
                                                {featuredBlog.author}
                                            </p>
                                            <p className="featured-blog-date">
                                                {featuredBlog.date}
                                            </p>
                                        </div>

                                        <button onClick={() => navigate(`/blogs/${featuredBlog.slug}`)}>
                                            READ MORE
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <section className="blog-section" id="blogs">
                <h1>Table of Continents</h1>

                <div className="continent-filters" aria-label="Filter blogs by continent">
                    {categoryFilters.map((category) => (
                        <button
                            type="button"
                            key={category}
                            className={selectedTag === category ? "active" : ""}
                            aria-pressed={selectedTag === category}
                            onClick={() => setSelectedTag(category)}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                <div className={`blog-scroll-wrapper ${showAllBlogs ? "expanded" : ""}`}>
                    <div className="blog-list">
                        {blogsLoading && <p className="blog-list-status">Loading stories...</p>}
                        {!blogsLoading && blogsError && (
                            <p className="blog-list-status blog-list-error">{blogsError}</p>
                        )}
                        {!blogsLoading && !blogsError && blogs.length === 0 && (
                            <p className="blog-list-status">No stories found in this category.</p>
                        )}

                        {!blogsLoading && !blogsError && blogs.length > 0 && (
                            <div className="continent-blog-list">
                                {blogs.map((blog, index) => {
                                    const imageOnLeft = index % 2 === 0;

                                    return (
                                        <article
                                            className={`continent-blog-card ${imageOnLeft ? "image-left" : "image-right"}`}
                                            key={blog.slug || `${blog.title}-${index}`}
                                        >
                                            <div className="continent-blog-image-wrap">
                                                <img
                                                    src={require(`../images/${blog.image}`)}
                                                    alt={blog.title}
                                                    className="continent-blog-image"
                                                />
                                            </div>

                                            <div className="continent-blog-content">
                                                <div className="continent-blog-categories">
                                                    {(blog.categories ?? []).map((category) => (
                                                        <span key={category}>{category}</span>
                                                    ))}
                                                </div>
                                                <h2>{blog.title}</h2>
                                                <p className="continent-blog-meta">
                                                    <span>by {blog.author}</span>
                                                    <span>{blog.date}</span>
                                                </p>
                                                <p className="continent-blog-excerpt">{blog.excerpt}</p>
                                                <button
                                                    type="button"
                                                    className="continent-read-more"
                                                    onClick={() => navigate(`/blogs/${blog.slug}`)}
                                                    aria-label={`Read more about ${blog.title}`}
                                                >
                                                    READ MORE <span aria-hidden="true">&#8594;</span>
                                                </button>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {!blogsLoading && !blogsError && blogs.length > 0 && (
                        <button
                            type="button"
                            className="expand-blogs-button"
                            aria-expanded={showAllBlogs}
                            onClick={() => setShowAllBlogs((isExpanded) => !isExpanded)}
                        >
                            <span>{showAllBlogs ? "Collapse" : `Expand all (${blogs.length})`}</span>
                            <span className="expand-blogs-chevron" aria-hidden="true">
                                {showAllBlogs ? "⌃" : "⌄"}
                            </span>
                        </button>
                    )}
                </div>
            </section>
        </div>
    );
}

export default BlogPage;
